import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ThinkingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin: 1rem 0;
`;

const ThinkingText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #e81313;
  margin-bottom: 1rem;
  min-height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThinkingTextWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  position: relative;
  white-space: nowrap;
`;

const ThinkingTextContent = styled.span`
  display: inline-block;
`;

const ThinkingDots = styled.span`
  display: inline-block;
  min-width: 1.5rem;
  text-align: left;
  
  @keyframes thinkingDots {
    0%, 20% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
  
  animation: thinkingDots 1.5s infinite;
`;

const ThinkingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(232, 19, 19, 0.3);
  border-top: 2px solid #e81313;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface LoadingAnimationProps {
  texts?: string[];
  text?: string; // Single text for simple mode (tables)
  dotInterval?: number; // Interval for dot animation in milliseconds
}

export const LoadingAnimation: React.FC<LoadingAnimationProps> = ({ 
  texts = ['Processing data', 'Generating insights', 'Thinking'],
  text,
  dotInterval = 1000
}) => {
  // Use single text if provided, otherwise use first text from array
  const [currentText, setCurrentText] = useState(text || texts[0] || 'Processing data');
  const [dotCount, setDotCount] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4);
    }, dotInterval);
    
    return () => clearInterval(interval);
  }, [dotInterval]);
  
  // Only rotate text if multiple texts provided and no single text specified
  useEffect(() => {
    if (text || texts.length <= 1) return;
    
    const textInterval = setInterval(() => {
      setCurrentText(prev => {
        const currentIndex = texts.indexOf(prev);
        return texts[(currentIndex + 1) % texts.length];
      });
    }, 2000);
    
    return () => clearInterval(textInterval);
  }, [texts, text]);
  
  // Update currentText when text prop changes
  useEffect(() => {
    if (text) {
      setCurrentText(text);
    }
  }, [text]);
  
  return (
    <ThinkingContainer>
      <ThinkingSpinner />
      <ThinkingText>
        <ThinkingTextWrapper>
          <ThinkingTextContent>{currentText}</ThinkingTextContent>
          <ThinkingDots>
            {'.'.repeat(dotCount)}
          </ThinkingDots>
        </ThinkingTextWrapper>
      </ThinkingText>
    </ThinkingContainer>
  );
};

